import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  PUBLIC_REVIEW_SORTS,
  REVIEW_TEXT_MAX,
  type MyReview,
  type MyReviews,
  type PendingRating,
  type PublicReview,
  type PublicReviewPage,
  type ReviewBlock,
  type ReviewPhoto,
} from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import {
  editReview,
  fetchMyReviews,
  fetchPublicReviews,
  reviewBlock,
  submitReview,
  uploadReviewPhoto,
} from '../api/reviews';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';
import { newOpKey } from './supportMerge';
import {
  EMPTY_RATE_FORM,
  MAX_PHOTOS,
  canStillEdit,
  cycleDish,
  formFromReview,
  isEmptyRateForm,
  markPrompted,
  recallDishes,
  rememberDishes,
  reviewPhotoProblem,
  wasPrompted,
  type DishRef,
  type RateForm,
} from './reviewRules';

/*
 * Ratings + reviews state (W17). Screens stay presentation-only: every fetch, cache rule,
 * optimistic change, upload and draft lives here. Reads are react-query (stale-while-revalidate);
 * a submitted rating shows at once and rolls back if the server refuses.
 */

export const MY_REVIEWS_KEY = ['my-reviews'] as const;
const publicKey = (outletId: string, sort: PublicReviewSort) =>
  ['reviews', outletId, sort] as const;

export type PublicReviewSort = (typeof PUBLIC_REVIEW_SORTS)[number];
export const REVIEW_SORTS: readonly PublicReviewSort[] = PUBLIC_REVIEW_SORTS;
const PAGE_SIZE = 20;

/* ---------------- my reviews + orders waiting for a rating ---------------- */

export function useMyReviews(enabled: boolean) {
  const query = useQuery({
    queryKey: MY_REVIEWS_KEY,
    queryFn: fetchMyReviews,
    enabled,
    staleTime: 30_000,
    // Trust off on the server (404): the screens simply show nothing to rate.
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
  });
  const pending = useMemo(() => query.data?.pending ?? [], [query.data]);
  const reviews = useMemo(() => query.data?.reviews ?? [], [query.data]);

  // The dish list only comes with pending orders: keep it so a later edit can show the chips.
  useEffect(() => {
    for (const item of pending) rememberDishes(item.orderId, item.dishes);
  }, [pending]);

  return {
    loading: enabled && query.isLoading,
    failed: query.isError && errorStatus(query.error) !== 404,
    retry: () => void query.refetch(),
    pending,
    reviews,
    pendingFor: (orderId: string) => pending.find((item) => item.orderId === orderId) ?? null,
    reviewFor: (orderId: string) => reviews.find((item) => item.orderId === orderId) ?? null,
  };
}

/** What the rate sheet is about: a new rating, or an edit of an existing one. */
export type RateTarget =
  { kind: 'new'; pending: PendingRating } | { kind: 'edit'; review: MyReview };

/**
 * Order page: the rating card (rate / your rating / edit), and the sheet opening by itself once
 * per order right after delivery.
 */
export function useOrderRating(orderId: string, enabled: boolean) {
  const mine = useMyReviews(enabled);
  const pending = mine.pendingFor(orderId);
  const review = mine.reviewFor(orderId);
  const [target, setTarget] = useState<RateTarget | null>(null);

  useEffect(() => {
    if (!enabled || !pending || wasPrompted(orderId)) return;
    markPrompted(orderId);
    setTarget({ kind: 'new', pending });
  }, [enabled, pending, orderId]);

  return {
    pending,
    review,
    editable: review ? canStillEdit(review) : false,
    target,
    openRate: () => pending && setTarget({ kind: 'new', pending }),
    openEdit: () => review && setTarget({ kind: 'edit', review }),
    close: () => setTarget(null),
  };
}

/* ---------------- photos (review sheet) ---------------- */

export interface ReviewPhotoSlot {
  key: string;
  /** Server URL for kept photos, object URL for new ones (shown at once). */
  preview: string;
  photoId: string | null;
  /** 0–100 while uploading, null when done or failed. */
  progress: number | null;
  failed: boolean;
}

export interface ReviewPhotoMessages {
  badType: string;
  tooBig: string;
  tooMany: string;
  uploadFailed: string;
}

function useReviewPhotos(initial: ReviewPhoto[], messages: ReviewPhotoMessages) {
  const [slots, setSlots] = useState<ReviewPhotoSlot[]>(() =>
    initial.map((photo) => ({
      key: `kept-${photo.id}`,
      preview: photo.url,
      photoId: photo.id,
      progress: null,
      failed: false,
    })),
  );
  const objectUrls = useRef(new Set<string>());

  useEffect(() => {
    const urls = objectUrls.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const patch = (key: string, change: Partial<ReviewPhotoSlot>) =>
    setSlots((current) =>
      current.map((slot) => (slot.key === key ? { ...slot, ...change } : slot)),
    );

  const add = (file: File) => {
    const problem = reviewPhotoProblem(file);
    if (problem) {
      showToast(problem === 'type' ? messages.badType : messages.tooBig, { tone: 'negative' });
      return;
    }
    if (slots.length >= MAX_PHOTOS) {
      showToast(messages.tooMany, { tone: 'negative' });
      return;
    }
    const key = newOpKey('rphoto');
    const preview = URL.createObjectURL(file);
    objectUrls.current.add(preview);
    setSlots((current) => [
      ...current,
      { key, preview, photoId: null, progress: 0, failed: false },
    ]);
    uploadReviewPhoto(file, (percent) => patch(key, { progress: percent }))
      .then((done) => {
        if (!done.uploaded) throw new Error('not uploaded');
        patch(key, { photoId: done.photoId, progress: null });
      })
      .catch(() => {
        patch(key, { progress: null, failed: true });
        showToast(messages.uploadFailed, { tone: 'negative' });
      });
  };

  const remove = (key: string) =>
    setSlots((current) => {
      const slot = current.find((entry) => entry.key === key);
      if (slot && objectUrls.current.has(slot.preview)) {
        URL.revokeObjectURL(slot.preview);
        objectUrls.current.delete(slot.preview);
      }
      return current.filter((entry) => entry.key !== key);
    });

  const ready = slots.filter((slot) => slot.photoId !== null);
  return {
    slots,
    add,
    remove,
    uploading: slots.some((slot) => slot.progress !== null),
    photoIds: ready.map((slot) => slot.photoId as string),
    photos: ready.map((slot) => ({ id: slot.photoId as string, url: slot.preview })),
    canAdd: slots.length < MAX_PHOTOS,
  };
}

/* ---------------- the rate sheet ---------------- */

export interface RateMessages extends ReviewPhotoMessages {
  thanks: string;
  updated: string;
  changed: string;
  failed: string;
}

/**
 * One rating (new or edit): food + delivery stars, dish likes, text, up to 3 photos. The typed
 * form is draft-saved per order (Keep / Discard) and cleared once saved; photos are not drafted.
 * The list behind the sheet updates at once and rolls back when the server refuses; a refusal
 * with a reason (`details.block`) shows that reason in the sheet.
 */
export function useRateSheet(target: RateTarget, messages: RateMessages, onDone: () => void) {
  const client = useQueryClient();
  const isEdit = target.kind === 'edit';
  const orderId = isEdit ? target.review.orderId : target.pending.orderId;
  const outletId = isEdit ? target.review.outletId : target.pending.outletId;
  const outletName = isEdit ? target.review.outletName : target.pending.outletName;
  const dishes: DishRef[] = useMemo(
    () => (isEdit ? recallDishes(orderId) : target.pending.dishes),
    [isEdit, orderId, target],
  );
  // No rider (e.g. own delivery): delivery stars are skipped. Edits keep what the review had.
  const hasRider = isEdit ? target.review.deliveryStars !== null : target.pending.hasRider;

  const base = useMemo<RateForm>(
    () => (isEdit ? formFromReview(target.review) : EMPTY_RATE_FORM),
    [isEdit, target],
  );
  const [form, setForm] = useState<RateForm>(base);
  const [block, setBlock] = useState<ReviewBlock | null>(null);
  const draft = useFormDraft<RateForm>(`review:${orderId}`, form, {
    enabled: true,
    isEmpty: isEmptyRateForm,
    base,
  });
  const photos = useReviewPhotos(isEdit ? target.review.photos : [], messages);
  const opKey = useRef(newOpKey('review'));

  const set = useCallback(
    (change: Partial<RateForm>) => setForm((current) => ({ ...current, ...change })),
    [],
  );

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        foodStars: form.foodStars,
        deliveryStars: hasRider && form.deliveryStars > 0 ? form.deliveryStars : null,
        text: form.text.trim() ? form.text.trim() : undefined,
        photoIds: photos.photoIds,
        likedDishIds: form.liked,
        unlikedDishIds: form.unliked,
      };
      return target.kind === 'edit'
        ? editReview(target.review.id, { ...body, expectedVersion: target.review.version })
        : submitReview({ ...body, orderId }, opKey.current);
    },
    onMutate: async () => {
      setBlock(null);
      await client.cancelQueries({ queryKey: MY_REVIEWS_KEY });
      const before = client.getQueryData<MyReviews>(MY_REVIEWS_KEY);
      if (before) {
        const now = new Date();
        const shown: MyReview =
          target.kind === 'edit'
            ? {
                ...target.review,
                foodStars: form.foodStars,
                deliveryStars: hasRider && form.deliveryStars > 0 ? form.deliveryStars : null,
                text: form.text.trim() || null,
                photos: photos.photos,
                likedDishIds: form.liked,
                unlikedDishIds: form.unliked,
              }
            : {
                id: `local-${orderId}`,
                orderId,
                orderNo: target.pending.orderNo,
                outletId,
                outletName,
                foodStars: form.foodStars,
                deliveryStars: hasRider && form.deliveryStars > 0 ? form.deliveryStars : null,
                text: form.text.trim() || null,
                photos: photos.photos,
                likedDishIds: form.liked,
                unlikedDishIds: form.unliked,
                status: 'published',
                editableUntil: new Date(now.getTime() + 24 * 3_600_000).toISOString(),
                reply: null,
                createdAt: now.toISOString(),
                version: 1,
              };
        client.setQueryData<MyReviews>(MY_REVIEWS_KEY, {
          pending: before.pending.filter((item) => item.orderId !== orderId),
          reviews: [shown, ...before.reviews.filter((item) => item.orderId !== orderId)],
        });
      }
      return { before };
    },
    onError: (error, _vars, context) => {
      if (context?.before) client.setQueryData(MY_REVIEWS_KEY, context.before);
      const refused = reviewBlock(error);
      if (refused) {
        setBlock(refused);
        if (refused === 'already_reviewed')
          void client.invalidateQueries({ queryKey: MY_REVIEWS_KEY });
        return;
      }
      if (errorStatus(error) === 409) {
        showToast(messages.changed, { tone: 'negative' });
        void client.invalidateQueries({ queryKey: MY_REVIEWS_KEY });
        return;
      }
      showToast(errorMessage(error, messages.failed), { tone: 'negative', ref: errorRef(error) });
    },
    onSuccess: (saved) => {
      client.setQueryData<MyReviews>(MY_REVIEWS_KEY, (data) =>
        data
          ? {
              pending: data.pending.filter((item) => item.orderId !== saved.orderId),
              reviews: [saved, ...data.reviews.filter((item) => item.orderId !== saved.orderId)],
            }
          : data,
      );
      draft.clear();
      opKey.current = newOpKey('review');
      showToast(isEdit ? messages.updated : messages.thanks, { tone: 'positive' });
      onDone();
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: MY_REVIEWS_KEY });
      void client.invalidateQueries({ queryKey: ['reviews', outletId] });
      void client.invalidateQueries({ queryKey: ['restaurant', outletId] });
    },
  });

  const text = form.text.slice(0, REVIEW_TEXT_MAX);
  return {
    isEdit,
    outletName,
    dishes,
    hasRider,
    form: { ...form, text },
    setFoodStars: (stars: number) => set({ foodStars: stars }),
    setDeliveryStars: (stars: number) => set({ deliveryStars: stars }),
    setText: (value: string) => set({ text: value.slice(0, REVIEW_TEXT_MAX) }),
    like: (menuItemId: string) =>
      setForm((current) => ({ ...current, ...cycleDish(current, menuItemId, 'like') })),
    unlike: (menuItemId: string) =>
      setForm((current) => ({ ...current, ...cycleDish(current, menuItemId, 'unlike') })),
    maxLength: REVIEW_TEXT_MAX,
    photos,
    block,
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const kept = draft.keep();
      if (kept) setForm(kept);
    },
    discardDraft: draft.discard,
    draftSaved: !isEmptyRateForm(form) && JSON.stringify(form) !== JSON.stringify(base),
    canSubmit:
      form.foodStars > 0 &&
      !photos.uploading &&
      !mutation.isPending &&
      block !== 'already_reviewed',
    submitting: mutation.isPending,
    submit: () => mutation.mutate(),
  };
}

/* ---------------- a restaurant's public reviews ---------------- */

/**
 * Rating summary + reviews of one restaurant, paged (20 at a time) with a sort. The restaurant
 * page and the full reviews page share the cache, so opening "See all" is instant.
 */
export function usePublicReviews(outletId: string, initialSort: PublicReviewSort = 'recent') {
  const [sort, setSort] = useState<PublicReviewSort>(initialSort);
  const query = useInfiniteQuery({
    queryKey: publicKey(outletId, sort),
    queryFn: ({ pageParam }) =>
      fetchPublicReviews(outletId, { sort, page: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last: PublicReviewPage) => (last.hasMore ? last.page + 1 : undefined),
    enabled: outletId !== '',
    staleTime: 60_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
  });
  const data: InfiniteData<PublicReviewPage> | undefined = query.data;
  const rows = useMemo<PublicReview[]>(() => {
    const seen = new Set<string>();
    const out: PublicReview[] = [];
    for (const page of data?.pages ?? []) {
      for (const review of page.reviews) {
        if (seen.has(review.id)) continue;
        seen.add(review.id);
        out.push(review);
      }
    }
    return out;
  }, [data]);

  const fetchNext = query.fetchNextPage;
  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void fetchNext();
  }, [query.hasNextPage, query.isFetchingNextPage, fetchNext]);

  return {
    loading: query.isLoading,
    /** Trust is off on the server: hide the reviews block entirely. */
    unavailable: errorStatus(query.error) === 404,
    failed: query.isError && errorStatus(query.error) !== 404,
    retry: () => void query.refetch(),
    summary: data?.pages[0]?.summary ?? null,
    rows,
    sort,
    setSort,
    hasMore: Boolean(query.hasNextPage),
    loadingMore: query.isFetchingNextPage,
    loadMore,
  };
}

/** Calls `onVisible` whenever the sentinel scrolls into view (infinite paging). */
export function useLoadMoreSentinel(onVisible: () => void, active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !active || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onVisible();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, active]);
  return ref;
}
